import { forwardRef, useState } from 'react';

import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';
import { useGetTrialOfferCategory } from 'hooks/useRedeemCouponForTrialOffer';

import { BasicModal } from 'Components/BasicModal';
import { Image } from 'Components/Image';
import { Tooltip } from 'Components/PortalTooltip';

import { productsCategories, productsSlugs } from 'constants/products';

import { BackgroundVideo } from 'Blocks/BackgroundVideo';

import ProductCTA from 'Containers/ProductCTA';

import checkGreenCircle from 'assets/images/check-green-circle.svg';
import infoImgDark from 'assets/images/homeRevamp/dark-info-icon.svg';
import heroImageDesktop from 'assets/images/landingPages/densitySet/prose-custom-root-source-close-up-desktop.jpg';
import heroImageMobile from 'assets/images/landingPages/densitySet/prose-custom-root-source-close-up-mobile.jpg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent, trackPreselect } from 'dux/tracking/actions';
import {
  getTrialOfferFlagVariant,
  shouldShowTrialOffer,
  trialOfferEvergreen,
} from 'dux/featureFlags/selectors';
import { getHaveOrders } from 'dux/user/selectors';

const Separator = styled.div`
  width: 98%;
  border-top: 1px solid ${theme.colors.primary[200]};
  margin: 0 auto;
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
  background-color: ${theme.colors.highlight[100]};
  width: 100px;
  height: 100px;
  border-radius: 100%;
  flex-direction: column;
  padding: ${theme.spacing['2x']};

  ${mediaMinWidth('sm')} {
    top: 30px;
    right: unset;
    left: 30px;

    width: 140px;
    height: 140px;
  }
`;

const CloseModalButton = styled.button`
  width: 100%;

  padding: 0;
  appearance: none;
  background: none;
  border: none;

  cursor: pointer;
`;

const StyledModal = styled(BasicModal)`
  padding: ${theme.spacing['5x']};
  background-color: ${theme.colors.neutral[200]};
`;

const TooltipContent = ({ onClose }: { onClose?: () => void }) => (
  <>
    <Text variant="body5regular">
      Based on self-assessment in a 4-week use-test of 97 subjects who used Prose Custom Scalp Serum
      daily.
    </Text>
    {onClose && (
      <CloseModalButton onClick={onClose}>
        <Text marginLeft="auto" variant="label3regular" width="fit">
          close x
        </Text>
      </CloseModalButton>
    )}
  </>
);

const CtaWithMessaging = () => {
  const hasOrders = useAppSelector(getHaveOrders);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  return (
    <Box alignItems="center" display="flex" flexDirection="column" gap="3x">
      <Box width="full">
        <ProductCTA
          customOnClick={(redirectUrl: string) =>
            dispatch(
              // @ts-expect-error dux not migrated yet
              trackPreselect({
                productSlug: productsSlugs.SCALP_SERUM,
                locationId: 'pdp-density_set',
                productCategory: productsCategories.HAIRCARE,
                onSuccess: () => {
                  dispatch(
                    // @ts-expect-error dux not migrated yet
                    trackPreselect({
                      productSlug: productsSlugs.SUPPLEMENT_CORE,
                      locationId: 'pdp-density_set',
                      // We want to make sure that the breadcrumbs call is finished before starting a cart API call
                      onSuccess: () => navigate(redirectUrl),
                      productCategory: productsCategories.HAIRCARE,
                    }),
                  );
                },
              }),
            )
          }
          dataFrom="density-set-lp-hero"
          fullWidth
          noMargin
          productCategory={productsCategories.HAIRCARE}
          theme="roundedBlack"
        />
      </Box>
      {!hasOrders && (
        <Text color="neutral.800" textAling="center" variant="body4regular">
          Love your first order (or it’s on us){' '}
        </Text>
      )}
    </Box>
  );
};

const Header = () => {
  const { isMobile, isTablet } = useResponsiveVariant({ legacy: false });

  return (
    <Box display="flex" flexDirection="column" gap={{ desktop: '3x' }}>
      <Text as="h1" variant={isMobile ? 'h3' : 'h2'}>
        The Custom
        {!isTablet && <br />} Hair Density Set
      </Text>
      <Text
        color="primary.400"
        marginTop={{ desktop: 'none', mobile: '2x' }}
        variant="body3regular"
      >
        Custom Hair Supplements + Scalp Serum
      </Text>
    </Box>
  );
};

export const Hero = forwardRef<HTMLDivElement>((_, ref) => {
  // @ts-expect-error selector not migrated yet
  const trialOfferVariant: boolean | string = useAppSelector(getTrialOfferFlagVariant);
  const showTrialOffer = useAppSelector(shouldShowTrialOffer);

  const [openedModal, setOpenedModal] = useState<'hero-tooltip' | undefined>(undefined);
  const dispatch = useAppDispatch();
  const eligibleCategoryForTrialOffer: (typeof productsCategories)[keyof typeof productsCategories] =
    useGetTrialOfferCategory();
  const trialOfferEligibility =
    (showTrialOffer && eligibleCategoryForTrialOffer === productsCategories.HAIRCARE) ||
    eligibleCategoryForTrialOffer === productsCategories.AGNOSTIC;

  const { isMobile, isTablet, isDesktop } = useResponsiveVariant({ legacy: false });
  return (
    <Box ref={ref} width="full">
      <StyledModal
        hideClose
        isOpen={openedModal === 'hero-tooltip' || false}
        onClose={() => setOpenedModal(undefined)}
      >
        <TooltipContent onClose={() => setOpenedModal(undefined)} />
      </StyledModal>

      <Box
        display="flex"
        flexDirection={{ mobile: 'column', tablet: 'column', desktop: 'row' }}
        gap="6x"
        paddingX="8x"
        paddingY="12x"
        w="full"
      >
        {!isDesktop && <Header />}
        <Box
          borderRadius="sm"
          display="flex"
          flexBasis={{ mobile: undefined, tablet: undefined, desktop: 0 }}
          flexGrow={1}
          overflow="hidden"
          position="relative"
          w="full"
        >
          {trialOfferEligibility && (
            <Badge>
              <Text color="primary.400" variant={isMobile ? 'body3medium' : 'body1medium'}>
                60% OFF
              </Text>
              <Text
                color="primary.400"
                textAlign="center"
                variant={isMobile ? 'body5regular' : 'body4regular'}
              >
                {trialOfferVariant === trialOfferEvergreen
                  ? 'your first Density Set'
                  : 'your first set + free gift'}
              </Text>
            </Badge>
          )}

          <BackgroundVideo
            autoplayDelay={1000}
            dataFrom="density-set-lp-hero-video"
            fitCover
            imageAlt="Close up of Prose Custom Root Source bottle with greenery in background"
            isLegacyBreakpoints={false}
            posterImages={{
              mobile: heroImageMobile,
              tablet: heroImageMobile,
              desktop: heroImageDesktop,
            }}
            trackingText="Density Set Landing Video"
            videoSources={{
              mobile: 'density-set-hero-mobile',
              tablet: 'density-set-hero-mobile',
              desktop: 'density-set-hero-desktop',
            }}
          />
        </Box>
        <Box paddingX={{ desktop: '6x' }} paddingY={{ desktop: '6x' }}>
          <Box
            __minWidth={isDesktop && '400px'}
            __width={isDesktop && '400px'}
            borderRadius="sm"
            display="flex"
            flexDirection="column"
            gap="6x"
          >
            {!isDesktop && <CtaWithMessaging />}
            <Box
              display="flex"
              flexDirection="column"
              gap={{ mobile: '3x', tablet: '3x', desktop: '6x' }}
              w="full"
            >
              {isDesktop && (
                <>
                  <Header />
                  <Separator />
                </>
              )}

              <Text color="primary.400" variant={isMobile ? 'body4regular' : 'body3regular'}>
                A personalized, 2-step system to promote visibly fuller, denser, healthier-looking
                hair.
              </Text>

              <Box display="flex" flexDirection="column" gap="2x">
                <Box alignItems="center" display="flex" flexDirection="row" gap="4x" paddingY="1x">
                  <Image alt="check green circle" height={18} src={checkGreenCircle} width={18} />

                  <Box display="flex" flexDirection="row" gap="2x">
                    <Text color="primary.400" variant={isMobile ? 'body4regular' : 'body3regular'}>
                      Healthier scalp + stronger hair in 4 weeks
                    </Text>

                    <Box alignItems="center" display="flex" paddingBottom="1x" position="relative">
                      {isMobile ? (
                        <Image
                          alt="Tooltip icon"
                          data-click="tooltip"
                          data-from="density-set-lp-hero"
                          height={16}
                          onClick={() => {
                            setOpenedModal('hero-tooltip');
                            dispatch(trackHeapEvent('Hair Density Set Module Tooltip clicked'));
                          }}
                          src={infoImgDark}
                          width={16}
                        />
                      ) : (
                        <Tooltip
                          arrowSize={10}
                          content={<TooltipContent />}
                          dataClick="tooltip"
                          dataFrom="density-set-lp-hero"
                          hasBeforePointer={false}
                          maxWidth={238}
                          openerComponent={
                            <Image alt="" height={16} src={infoImgDark} width={16} />
                          }
                          openerHeight={16}
                          openerWidth={16}
                          style={{
                            padding: theme.spacing['5x'],
                            fontSize: '12px',
                            lineHeight: 'initial',
                            backgroundColor: theme.colors.neutral[200],
                          }}
                          topPlus={0}
                          trackingText="Hair Density Set Module Tooltip"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="row" gap="4x" paddingY="1x">
                  <Image alt="check green circle" height={18} src={checkGreenCircle} width={18} />
                  <Text color="primary.400" variant={isMobile ? 'body4regular' : 'body3regular'}>
                    Dermatologist tested
                  </Text>
                </Box>

                <Box display="flex" flexDirection="row" gap="4x" paddingY="1x">
                  <Image alt="check green circle" height={18} src={checkGreenCircle} width={18} />
                  <Text color="primary.400" variant={isMobile ? 'body4regular' : 'body3regular'}>
                    Personalized to 85+ factors — {!isTablet && <br />}
                    from what you eat to where you live
                  </Text>
                </Box>
              </Box>
            </Box>
            {isDesktop && (
              <>
                <Separator />
                <CtaWithMessaging />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

Hero.displayName = 'Hero';
